import { FC, Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Bars4Icon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, Navigate, Outlet, useLocation } from 'react-location';
import _ from 'lodash';
import routes from 'router/routes';
import { classNames, wrapClick } from 'utils';
import { Avatar } from 'components/core';
import { withRoutePermissions } from 'router/filter';
import { useAuthStore } from 'state/auth';

const AppLayout: FC = () => {
  const location = useLocation();
  const currentUser = useAuthStore(state => state.user);
  const currentToken = useAuthStore(state => state.token);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = _.chain(routes)
    .filter(['meta.layout', 'App'])
    .thru(withRoutePermissions(currentUser?.role?.permissions ?? []))
    .filter(item => !!item.sidebar)
    .value();

  if (!currentToken) {
    return <Navigate to={'/signin'} search={{ redirect: location.current.href }} replace />;
  }
  if (currentUser?.mustChangePassword && location.current.pathname !== '/change-password') {
    return <Navigate to={'/change-password'} search={{ redirect: location.current.href }} replace />;
  }
  return (
    <div className="h-screen">
      <div className="h-full flex">
        <Transition.Root show={mobileMenuOpen} as={Fragment}>
          <Dialog as="div" className="fixed inset-0 flex z-40 lg:hidden" onClose={setMobileMenuOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex-1 flex flex-col max-w-xs w-full bg-primary-700 focus:outline-none">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-4">
                    <button
                      type="button"
                      className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                {/* Header */}
                <div className="flex flex-shrink-0 items-center border-primary-600 p-4 flex-row">
                  <div className="ml-3 flex gap-x-3 items-center">
                    <div className="bg-white rounded-lg"></div>
                    <p className="text-xl text-white font-manrope font-bold">WireDesk</p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="py-4 flex flex-grow flex-col overflow-y-auto no-scrollbar">
                  <nav className="flex-1 space-y-1 px-2" aria-label="Sidebar">
                    {/* Menu Section */}
                    <div className="mb-6">
                      <p className="text-[10px] text-white mb-3">MENU</p>
                      {_.map(_.filter(navigation, ['sidebar.category', 'menu']), item => {
                        return (
                          <Link
                            key={item.sidebar?.label}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            getActiveProps={() => ({
                              className: 'bg-primary-800 text-white',
                            })}
                            getInactiveProps={() => ({
                              className: 'text-gray-300 hover:bg-primary-800 hover:text-white',
                            })}
                            className="group flex items-center px-2 py-2 text-sm rounded"
                          >
                            {item.sidebar?.icon && (
                              <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-4 w-4 opacity-90')} aria-hidden="true" />
                            )}
                            {item.sidebar?.label}
                          </Link>
                        );
                      })}
                    </div>

                    {/* App Section */}
                    <div className="pb-6">
                      <p className="text-[10px] text-white mb-3">APP</p>
                      {_.map(_.filter(navigation, ['sidebar.category', 'app']), item => {
                        return (
                          <Link
                            key={item.sidebar?.label}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            getActiveProps={() => ({
                              className: 'bg-primary-800 text-white',
                            })}
                            getInactiveProps={() => ({
                              className: 'text-gray-300 hover:bg-primary-800 hover:text-white',
                            })}
                            className="group flex items-center px-2 py-2 my-2 text-sm rounded"
                          >
                            {item.sidebar?.icon && (
                              <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-4 w-4 opacity-90')} aria-hidden="true" />
                            )}
                            {item.sidebar?.label}
                          </Link>
                        );
                      })}
                    </div>

                    {/* Settings Section */}
                    <div className="">
                      <p className="text-[10px] text-white mb-3">SETTINGS</p>
                      {_.map(_.filter(navigation, ['sidebar.category', 'settings']), item => {
                        return (
                          <Link
                            key={item.sidebar?.label}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            getActiveProps={() => ({
                              className: 'bg-primary-800 text-white',
                            })}
                            getInactiveProps={() => ({
                              className: 'text-gray-300 hover:bg-primary-800 hover:text-white',
                            })}
                            className="group flex items-center px-2 py-2 my-2 text-sm rounded"
                          >
                            {item.sidebar?.icon && (
                              <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-4 w-4 opacity-90')} aria-hidden="true" />
                            )}
                            {item.sidebar?.label}
                          </Link>
                        );
                      })}
                    </div>
                  </nav>
                </div>

                {/* Support & Secondary Navigation */}
                <div className="py-4 flex flex-shrink-0 flex-col">
                  <nav className="flex-1 space-y-1 px-2" aria-label="Sidebar">
                    <a
                      href="mailto:dodoo@wewire.com"
                      target="_blank"
                      className="group w-full flex items-center px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-primary-800 hover:text-white"
                      rel="noreferrer"
                    >
                      <EnvelopeIcon className={classNames('text-gray-200 group-hover:text-gray-50', 'mr-3 flex-shrink-0 h-6 w-6')} aria-hidden="true" />
                      Support
                    </a>
                    {_.map(_.filter(navigation, ['sidebar.level', 'secondary']), item => {
                      return item.sidebar?.onClick && _.isFunction(item.sidebar?.onClick) ? (
                        <button
                          key={item.sidebar?.label}
                          onClick={wrapClick(() => {
                            item.sidebar?.onClick?.();
                            setMobileMenuOpen(false);
                          })}
                          className="group w-full flex items-center px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-primary-800 hover:text-white"
                        >
                          {item.sidebar?.icon && <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-6 w-6 opacity-90')} aria-hidden="true" />}
                          {item.sidebar?.label}
                        </button>
                      ) : (
                        <Link
                          key={item.sidebar?.label}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          getActiveProps={() => ({
                            className: 'bg-primary-800 text-white',
                          })}
                          getInactiveProps={() => ({
                            className: 'text-gray-300 hover:bg-primary-800 hover:text-white',
                          })}
                          className="group flex items-center px-2 py-2 text-sm rounded"
                        >
                          {item.sidebar?.icon && <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-6 w-6 opacity-90')} aria-hidden="true" />}
                          {item.sidebar?.label}
                        </Link>
                      );
                    })}
                  </nav>
                </div>

                {/* User Profile Footer */}
                <div className="flex flex-shrink-0 border-t border-primary-600 p-4 overflow-hidden">
                  <div className="flex-1 flex items-center space-x-3 overflow-hidden">
                    <div className="flex-shrink-0">
                      <Avatar
                        src={currentUser?.avatar}
                        size="xs"
                        alt={[currentUser?.firstName || '', currentUser?.lastName || ''].join(' ').trim() || 'N A'}
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="text-sm font-medium text-gray-200 group-hover:text-white line-clamp-1">
                        {[currentUser?.firstName || '', currentUser?.lastName || ''].join(' ').trim()}
                      </p>
                      <p className="text-xs font-medium text-gray-300 group-hover:text-gray-100 line-clamp-1 text-ellipsis overflow-hidden">
                        {currentUser?.email}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:flex lg:flex-shrink-0">
          <div className="flex flex-col border-r border-gray-200 bg-primary-700 w-64">
            <div className="flex flex-shrink-0 items-center border-primary-600 p-4 flex-row">
              <div className="ml-3 flex gap-x-3 items-center">
                <div className="bg-white rounded-lg"></div>
                <p className="text-xl text-white font-manrope font-bold ">WireDesk</p>
              </div>
            </div>
            <div className="py-4 flex flex-grow flex-col overflow-y-auto no-scrollbar">
              <nav className="flex-1 space-y-1 px-2" aria-label="Sidebar">
                <div className="mb-6">
                  <p className="text-[10px] text-white mb-3">MENU</p>
                  {_.map(_.filter(navigation, ['sidebar.category', 'menu']), item => {
                    return (
                      <Link
                        key={item.sidebar?.label}
                        to={item.path}
                        getActiveProps={() => ({
                          className: 'bg-primary-800 text-white',
                        })}
                        getInactiveProps={() => ({
                          className: 'text-gray-300 hover:bg-primary-800 hover:text-white',
                        })}
                        className="group flex items-center px-2 py-2 text-sm rounded"
                      >
                        {item.sidebar?.icon && (
                          <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-4 w-4 opacity-90')} aria-hidden="true" />
                        )}
                        {item.sidebar?.label}
                      </Link>
                    );
                  })}
                </div>

                <div className="pb-6">
                  <p className="text-[10px] text-white mb-3">APP</p>
                  {_.map(_.filter(navigation, ['sidebar.category', 'app']), item => {
                    return (
                      <Link
                        key={item.sidebar?.label}
                        to={item.path}
                        getActiveProps={() => ({
                          className: 'bg-primary-800 text-white',
                        })}
                        getInactiveProps={() => ({
                          className: 'text-gray-300 hover:bg-primary-800 hover:text-white',
                        })}
                        className="group flex items-center px-2 py-2 my-2 text-sm rounded"
                      >
                        {item.sidebar?.icon && (
                          <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-4 w-4 opacity-90')} aria-hidden="true" />
                        )}
                        {item.sidebar?.label}
                      </Link>
                    );
                  })}
                </div>

                <div className="">
                  <p className="text-[10px] text-white mb-3">SETTINGS</p>
                  {_.map(_.filter(navigation, ['sidebar.category', 'settings']), item => {
                    return (
                      <Link
                        key={item.sidebar?.label}
                        to={item.path}
                        getActiveProps={() => ({
                          className: 'bg-primary-800 text-white',
                        })}
                        getInactiveProps={() => ({
                          className: 'text-gray-300 hover:bg-primary-800 hover:text-white',
                        })}
                        className="group flex items-center px-2 py-2 my-2 text-sm rounded"
                      >
                        {item.sidebar?.icon && (
                          <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-4 w-4 opacity-90')} aria-hidden="true" />
                        )}
                        {item.sidebar?.label}
                      </Link>
                    );
                  })}
                </div>
              </nav>
            </div>
            <div className="py-4 flex flex-shrink-0 flex-col">
              <nav className="flex-1 space-y-1 px-2" aria-label="Sidebar">
                <a
                  href="mailto:dodoo@wewire.com"
                  target="_blank"
                  className="group w-full flex items-center px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-primary-800 hover:text-white"
                  rel="noreferrer"
                >
                  <EnvelopeIcon className={classNames('text-gray-200 group-hover:text-gray-50', 'mr-3 flex-shrink-0 h-6 w-6')} aria-hidden="true" />
                  Support
                </a>
                {_.map(_.filter(navigation, ['sidebar.level', 'secondary']), item => {
                  return item.sidebar?.onClick && _.isFunction(item.sidebar?.onClick) ? (
                    <button
                      key={item.sidebar?.label}
                      onClick={wrapClick(item.sidebar.onClick)}
                      className="group w-full flex items-center px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-primary-800 hover:text-white"
                    >
                      {item.sidebar?.icon && <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-6 w-6 opacity-90')} aria-hidden="true" />}
                      {item.sidebar?.label}
                    </button>
                  ) : (
                    <Link
                      key={item.sidebar?.label}
                      to={item.path}
                      getActiveProps={() => ({
                        className: 'bg-gray-100 text-gray-900',
                      })}
                      getInactiveProps={() => ({
                        className: 'text-gray-600 hover:bg-gray-50 hover:text-gray-800',
                      })}
                      className="group flex items-center px-2 py-2 text-sm rounded"
                    >
                      {item.sidebar?.icon && <item.sidebar.icon className={classNames('mr-3 flex-shrink-0 h-6 w-6 opacity-90')} aria-hidden="true" />}
                      {item.sidebar?.label}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex flex-shrink-0 border-t border-primary-600 p-4 overflow-hidden">
              <div className="flex-1 flex items-center space-x-3 overflow-hidden">
                <div className="flex-shrink-0">
                  <Avatar
                    src={currentUser?.avatar}
                    size="xs"
                    alt={[currentUser?.firstName || '', currentUser?.lastName || ''].join(' ').trim() || 'N A'}
                  />
                </div>
                <div className="flex-1 overflow-hidden">
                  <p className="text-sm font-medium text-gray-200 group-hover:text-white line-clamp-1">
                    {[currentUser?.firstName || '', currentUser?.lastName || ''].join(' ').trim()}
                  </p>
                  <p className="text-xs font-medium text-gray-300 group-hover:text-gray-100 line-clamp-1 text-ellipsis overflow-hidden">
                    {currentUser?.email}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Mobile top navigation */}
          <div className="lg:hidden">
            <div className="bg-primary-600 py-2 px-4 flex items-center justify-between sm:px-6 lg:px-8">
              <div>
                <Avatar
                  src={currentUser?.avatar}
                  alt={currentUser?.fullName || `${currentUser?.firstName} ${currentUser?.lastName}` || 'N A'}
                  size="xs"
                />
              </div>
              <div>
                <button
                  type="button"
                  className="-mr-3 h-12 w-12 inline-flex items-center justify-center bg-primary-600 rounded text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <Bars4Icon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Display content here */}
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
